import React, { FC, ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Badge, Paper, TablePagination, Tooltip } from '@material-ui/core';
import { createStyles, makeStyles, Theme, withStyles } from '@material-ui/core/styles';
import MaterialTable from 'material-table';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import dayjs from 'dayjs';
import {
	Lock,
	Snooze,
	SportsBaseball,
	SportsBasketball,
	SportsCricket,
	SportsFootball,
	SportsGolf,
	SportsMotorsports,
	SportsSoccer
} from '@material-ui/icons';

import type { RootState } from '../../store/store';
import { name as gameFacetSelector, setSorting } from '../../store/reducers/gameFacet';
import { useLanguageContext } from '../languages';
import { getLocalTimezone } from '../../utils/dateTransform';
import { Game } from '../../utils/types';
import StatusChip, { statusChipStyles } from '../common/StatusChip';
import CopyText from '../common/CopyText';
import commonMessages from '../common/messages';

const useStyles = makeStyles(() => ({
	channelList: {
		listStyleType: 'none',
		padding: 0,
		margin: 0
	},
	channelListItem: {
		marginBottom: 3
	}
}));

const StyledBadge = withStyles((theme: Theme) =>
	createStyles({
		badge: {
			right: 3,
			top: 6
		}
	})
)(Badge);

const ReportList: FC<ReportListProps> = (props) => {
	const { games, pageSize, page, count, onPageChange } = props;

	const classes = useStyles();
	const statusChipClasses = statusChipStyles();
	const dispatch = useDispatch();

	// Search facets come from the gameFacetSelector in the redux store
	const { sorting } = useSelector((state: RootState) => state[gameFacetSelector]);
	const { orderBy, orderDirection } = sorting;

	const intl = useIntl();
	const { tableLocalization } = useLanguageContext();

	const rowStyle = (rowData: Game): CSSProperties => {
		const styling: CSSProperties = {
			overflowWrap: 'break-word'
		};

		if (rowData.airings && rowData.airings.items.length > 0) {
			styling.backgroundColor = '#f5f5f5';
		}

		return styling;
	};

	const onOrderChange = (orderBy: number, orderDirection: 'asc' | 'desc'): void => {
		dispatch(setSorting({ orderBy, orderDirection }));
	};

	const renderSportIcon = (sport: string): JSX.Element => {
		let sportIcon = <SportsSoccer fontSize='large' color='action' />;
		switch (sport) {
			case 'AmericanFootball':
				sportIcon = <SportsFootball fontSize='large' color='action' />;
				break;
			case 'Baseball':
				sportIcon = <SportsBaseball fontSize='large' color='action' />;
				break;
			case 'Basketball':
				sportIcon = <SportsBasketball fontSize='large' color='action' />;
				break;
			case 'Cricket':
				sportIcon = <SportsCricket fontSize='large' color='action' />;
				break;
			case 'Golf':
				sportIcon = <SportsGolf fontSize='large' color='action' />;
				break;
			case 'F1':
				sportIcon = <SportsMotorsports fontSize='large' color='action' />;
				break;
		}
		return (
			<div style={{ display: 'inline-block', float: 'left', minHeight: '2rem', marginRight: '0.8rem', marginTop: '0.5rem' }}>
				<Tooltip title={sport}>{sportIcon}</Tooltip>
			</div>
		);
	};

	const columns = [
		{
			title: 'Game',
			sorting: false,
			cellStyle: {
				verticalAlign: 'top'
			},
			render: (row: Game): JSX.Element => (
				<>
					{renderSportIcon(row.sport!)}
					<div style={{ display: 'inline-block' }}>
						<CopyText tooltip='Copy Title'>{row.title!}</CopyText>
						<br />
						<CopyText tooltip='Copy Id'>{row.id}</CopyText>
					</div>
				</>
			)
		},
		{
			title: 'League',
			field: 'league.name',
			minWidth: 150,
			maxWidth: 150,
			cellStyle: {
				verticalAlign: 'top'
			},
			render: (row: Game): string => {
				const leagueName = row?.league?.name ?? '';
				return leagueName;
			},
			defaultSort: orderBy === 1 ? orderDirection : undefined
		},
		{
			title: `Start time (${getLocalTimezone()})`,
			field: 'startTime',
			align: 'center',
			minWidth: 120,
			maxWidth: 120,
			cellStyle: {
				verticalAlign: 'top'
			},
			render: (row: Game): JSX.Element => (
				<>
					<div>
						{row.startTime && dayjs(row.startTime).isValid() ? (
							<>
								<strong>{dayjs(row.startTime).format('DD MMM YYYY')}</strong> {dayjs(row.startTime).format('HH:mm')}
							</>
						) : (
							'Unknown'
						)}
					</div>
					{row.excludeScheduling === true && (
						<div style={{ marginTop: 2 }}>
							<Tooltip title='Excluded from auto-scheduling'>
								<Snooze color='action' style={{ fontSize: '2rem' }} />
							</Tooltip>
						</div>
					)}
				</>
			),
			defaultSort: orderBy === 2 ? orderDirection : undefined
		},
		{
			title: 'Status',
			field: 'status',
			sorting: false,
			align: 'center',
			minWidth: 120,
			maxWidth: 120,
			cellStyle: {
				verticalAlign: 'top'
			},
			render: (row: Game): JSX.Element => <StatusChip className={statusChipClasses.smallChip} status={row.status} />
		},
		{
			title: 'Airings',
			field: 'airings',
			sorting: false,
			align: 'center',
			minWidth: 180,
			maxWidth: 180,
			cellStyle: {
				verticalAlign: 'top'
			},
			render: (row: Game): JSX.Element => {
				if (row.airings?.items.length === 0) return <>None</>;
				return (
					<ul className={classes.channelList}>
						{row.airings?.items
							.sort((a, b): number => {
								if (!a.channel?.name || !b.channel?.name) return 0;
								return a.channel?.name > b.channel?.name ? 1 : -1;
							})
							.map((airing): JSX.Element => {
								if (
									airing.alternateIds?.items.length &&
									airing.alternateIds?.items.find((altId): boolean => altId.namespace === 'PROTECTED_AIRING')
								) {
									return (
										<li key={`airing_${airing.id}`} className={classes.channelListItem}>
											<StyledBadge
												badgeContent={
													<Tooltip title='Protected Airing'>
														<Lock color='action' style={{ fontSize: '1rem' }} />
													</Tooltip>
												}>
												<StatusChip className={statusChipClasses.bigChip} status={airing.status} name={airing.channel?.name || 'Unknown channel'} />
											</StyledBadge>
										</li>
									);
								}
								return (
									<li key={`airing_${airing.id}`} className={classes.channelListItem}>
										<StatusChip className={statusChipClasses.bigChip} status={airing.status} name={airing.channel?.name || 'Unknown channel'} />
									</li>
								);
							})}
					</ul>
				);
			}
		}
	];

	const options = {
		pageSize,
		search: false,
		sorting: true,
		rowStyle,
		tableLayout: 'fixed',
		emptyRowsWhenPaging: false
	};

	return (
		<MaterialTable
			title='Games Report'
			localization={tableLocalization}
			columns={columns}
			options={options}
			data={games}
			onOrderChange={onOrderChange}
			components={{
				Container: (props): JSX.Element => <Paper {...props} data-testid='games-table' />,
				Pagination: ({ style }): JSX.Element => (
					<TablePagination
						style={style}
						rowsPerPageOptions={[pageSize]}
						rowsPerPage={pageSize}
						count={count}
						page={page}
						onPageChange={(e, page): void => onPageChange(page)}
						labelDisplayedRows={({ from, to }): ReactNode =>
							intl.formatMessage(
								{
									...commonMessages.paginationLabel
								},
								{ from, to }
							)
						}
					/>
				)
			}}
		/>
	);
};

type ReportListProps = {
	games: Game[];
	pageSize: number;
	page: number;
	count: number;
	onPageChange: (page: number) => void;
	refetch: Function;
};

export default ReportList;
